// utils/statusColors.js
import { globalStyles } from "common/theme/styles";
const colors = globalStyles.colors;

const normalizeStatusKey = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const STATUS_META = {
  active: { color: "#166534", label: "Active" },
  inactive: { color: "#1E293B", label: "Inactive" },
  in_progress: { color: "#2563EB", label: "In Progress" },
  assigned: { color: "#D97706", label: "Assigned" },
  pending: { color: "#D97706", label: "Pending" },
  completed: { color: "#16A34A", label: "Completed" },
  success: { color: "#16A34A", label: "Success" },
  approved: { color: "#16A34A", label: "Approved" },
  accepted: { color: "#16A34A", label: "Accepted" },
  draft: { color: "#6B7280", label: "Draft" },
  not_started: { color: "#6B7280", label: "Not Started" },
  archived: { color: "#9CA3AF", label: "Archived" },
  closed: { color: "#6B7280", label: "Closed" },
  screening: { color: "#FBBF24", label: "Screening" },
  submissions: { color: "#3B82F6", label: "Submissions" },
  interview: { color: "#10B981", label: "Interview" },
  offered: { color: "#8B5CF6", label: "Offered" },
  hired: { color: "#059669", label: "Hired" },
  rejected: { color: "#EF4444", label: "Rejected" },
  scheduled: { color: "#3B82F6", label: "Scheduled" },
  feedback_submitted: { color: "#8B5CF6", label: "Feedback Submitted" },
  cancelled: { color: "#EF4444", label: "Cancelled" },
  rescheduled: { color: "#F59E0B", label: "Rescheduled" },
  submitted: { color: "#3B82F6", label: "Submitted" },
  shortlisted: { color: "#10B981", label: "Shortlisted" },
  r1_rejected: { color: "#EF4444", label: "R1 Rejected" },
  r2_rejected: { color: "#DC2626", label: "R2 Rejected" },
  final_rejected: { color: "#B91C1C", label: "Final Rejected" },
  withdrawn: { color: "#9CA3AF", label: "Withdrawn" },
  todo: { color: "#3965FF", label: "To Do" },
  on_hold: { color: "#EE5D50", label: "On Hold" },
  failed: { color: colors.errorText, label: "Failed" },
  viewed: { color: "#4338CA", label: "Viewed" },
  sent: { color: colors.cta["100"], label: "Sent" },
  pending_review: { color: "#C2410C", label: "Pending Review" },
  expired: { color: "#BE123C", label: "Expired" },
  declined: { color: "#B91C1C", label: "Declined" },
};

const STATUS_ALIASES = {
  inprogress: "in_progress",
  onhold: "on_hold",
};

const getNormalizedStatusMeta = (status) => {
  const normalizedStatus = normalizeStatusKey(status);
  const canonicalStatus = STATUS_ALIASES[normalizedStatus] || normalizedStatus;
  return STATUS_META[canonicalStatus];
};

const statusBadgeStyles = {
  active: { bg: "rgba(22, 101, 52, 0.12)", color: "#166534", borderColor: "rgba(22, 101, 52, 0.18)" },
  inactive: { bg: "rgba(100, 116, 139, 0.16)", color: "#1E293B", borderColor: "rgba(30, 41, 59, 0.22)" },
  draft: { bg: "rgba(148, 163, 184, 0.16)", color: "#475569", borderColor: "rgba(148, 163, 184, 0.28)" },
  not_started: { bg: "rgba(148, 163, 184, 0.16)", color: "#475569", borderColor: "rgba(148, 163, 184, 0.28)" },
  pending: { bg: "rgba(245, 158, 11, 0.14)", color: "#B45309", borderColor: "rgba(245, 158, 11, 0.24)" },
  pending_review: { bg: "rgba(249, 115, 22, 0.14)", color: "#C2410C", borderColor: "rgba(249, 115, 22, 0.24)" },
  in_progress: { bg: "rgba(60, 47, 192, 0.12)", color: colors.cta["100"], borderColor: "rgba(60, 47, 192, 0.20)" },
  viewed: { bg: "rgba(79, 70, 229, 0.12)", color: "#4338CA", borderColor: "rgba(79, 70, 229, 0.20)" },
  sent: { bg: "rgba(60, 47, 192, 0.12)", color: colors.cta["100"], borderColor: "rgba(60, 47, 192, 0.20)" },
  approved: { bg: "rgba(16, 185, 129, 0.12)", color: "#166534", borderColor: "rgba(22, 101, 52, 0.18)" },
  accepted: { bg: "rgba(16, 185, 129, 0.12)", color: "#166534", borderColor: "rgba(22, 101, 52, 0.18)" },
  completed: { bg: "rgba(16, 185, 129, 0.12)", color: "#166534", borderColor: "rgba(22, 101, 52, 0.18)" },
  rejected: { bg: "rgba(239, 68, 68, 0.12)", color: "#B91C1C", borderColor: "rgba(185, 28, 28, 0.18)" },
  declined: { bg: "rgba(239, 68, 68, 0.12)", color: "#B91C1C", borderColor: "rgba(185, 28, 28, 0.18)" },
  expired: { bg: "rgba(244, 63, 94, 0.12)", color: "#BE123C", borderColor: "rgba(190, 24, 93, 0.18)" },
  withdrawn: { bg: "rgba(100, 116, 139, 0.14)", color: "#475569", borderColor: "rgba(100, 116, 139, 0.22)" },
  on_hold: { bg: "rgba(234, 88, 12, 0.12)", color: "#C2410C", borderColor: "rgba(234, 88, 12, 0.18)" },
};

// Utility function to get color for a given status
export const getStatusColor = (status) => {
  const statusMeta = getNormalizedStatusMeta(status);
  if (!statusMeta) return `${colors.text["200"]} !important`;
  return `${statusMeta.color} !important`;
};

export const getSharedStatusPillStyle = (status) => {
  const sharedStyle = getNormalizedStatusMeta(status);

  if (sharedStyle) {
    return {
      ...sharedStyle,
      isMapped: true,
    };
  }

  return {
    color: "#475569",
    label: status || "-",
    isMapped: false,
  };
};

export const getStatusBadgeStyle = (status) => {
  const normalizedStatus = normalizeStatusKey(status);

  return (
    statusBadgeStyles[normalizedStatus] || {
      bg: "rgba(148, 163, 184, 0.16)",
      color: "#475569",
      borderColor: "rgba(148, 163, 184, 0.28)",
    }
  );
};

// Optional: array for dropdowns or components
export const statusData = [
  { name: "Completed", value: "completed", color: getStatusColor("completed") },
  { name: "To Do", value: "todo", color: getStatusColor("todo") },
  { name: "On Hold", value: "onHold", color: getStatusColor("onHold") },
  { name: "In Progress", value: "InProgress", color: getStatusColor("InProgress") },
  { name: "Pending", value: "pending", color: getStatusColor("pending") },
];
